import { Injectable, Inject, inject, SecurityContext } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { CommonService } from './common.service';
import { RestUrlsConfig } from '@signature-it/ngx-generic/config';
import { PageNotificationService } from './page-notification';
import { catchError, map } from 'rxjs/operators';
import {Observable, of, throwError} from 'rxjs';
import { SEODTO } from '@signature-it/ngx-generic/models';
import { ActivatedRoute} from '@angular/router';
import {GenericConfig} from "@signature-it/ngx-generic/config";
import {DomSanitizer} from '@angular/platform-browser';
import {ILanguage} from '@signature-it/ngx-generic/interfaces';
import {LanguageService} from "./language";
import {shareReplay} from "rxjs/operators";

declare let $: any;
declare let _: any;

@Injectable({
    providedIn: 'root'
})
export class SeoService_Generic extends CommonService {

    seoUrl = `${this.publicBaseUrl}/get_seo_by_url.json?url=`;
    cseoUrl = `${this.publicBaseUrl}/get_seo_by_campaign.json?name=`;
    eseoUrl = `${this.publicBaseUrl}/get_seo_by_entity.json?uri=`;
    sanitizer: DomSanitizer;
    langSvc: LanguageService;
    pageData: object = {};
    fileJson;
    schemaScript;

    constructor(
        protected http: HttpClient,
        protected restUrlsConf: RestUrlsConfig,
        protected notifySvc: PageNotificationService,
        protected meta: Meta,
        protected title: Title,
        @Inject(DOCUMENT) protected doc,
        protected route: ActivatedRoute
    ) {
        super(http, restUrlsConf, notifySvc);
        this.sanitizer = inject(DomSanitizer);
        this.langSvc = inject(LanguageService);
    }

    setSeo(title: string, description: string): void {
        this.meta.updateTag({name: 'description', content: description}, 'name=description');
        this.setOgTag('title', title);
        this.setOgTag('description', description);
        this.title.setTitle(title);
    }

	setPrerenderStatusCode(status: string) {
		this.meta.addTag({name: 'prerender-status-code', content: status});
    }
	removePrerenderStatusCode() {
		this.meta.removeTag('prerender-status-code');
	}

	setPrerenderHeader(content: string) {
		this.meta.addTag({name: 'prerender-header', content: content});
	}

    setCanonical() {
        if (this.doc.querySelector('link[rel="canonical"]')) {
            let pathname = location.pathname;
            let href = location.origin + this.fixPath(pathname);
            this.doc.querySelector('link[rel="canonical"]').setAttribute('href', this.sanitizer.sanitize(SecurityContext.URL,href));
        }
    }

    fixPath(pathname) {
        let searchParams = '';
        this.route.queryParams.subscribe(params => {
            if (params && params['campaign']) {
                searchParams = '?campaign=' + params['campaign'];
            }
        });
        if (pathname.startsWith('/product-page') || pathname.startsWith('/catalogue/product')) {
            let canonicalProductPagePath = GenericConfig.canonicalProductPagePath || 'product-page';
            pathname = pathname.replace(/(product-page|catalogue\/product)/, canonicalProductPagePath);
        }
        return decodeURIComponent(pathname) + searchParams;
    }

    setCanonicalByValue(canonicalValue) {
        this.doc.querySelector('link[rel="canonical"]').setAttribute('href', canonicalValue);
    }

    setHreflang() {
        if (GenericConfig.setHreflang) {
            this.langSvc.getCurrentLanguage$().subscribe((currLang: ILanguage) => {
                let currentUrl = this.langSvc.getFeUrlByLangCode(currLang);
                currentUrl = this.fixPath(currentUrl.split('?')[0]);
                this.langSvc.langList$.subscribe(languages => {
                    if (languages && languages.length) {
                        languages.forEach(lang => {
                            let url = location.href;
                            if (GenericConfig.isMultiLanguages) {
                                url = this.langSvc.feUrlByLangCode(lang.code, currentUrl);
                            }
                            url = url.replace(/(?<!:)\/{2,}/g, '/').replace(/\/$/, "");
                            let link = document.createElement('link');
                            link.setAttribute('rel', 'alternate');
                            link.setAttribute('href', this.sanitizer.sanitize(SecurityContext.URL, url));
                            link.setAttribute('hreflang', lang.code);
                            $('head').append(link);
                        });
                    }
                });
            });
        }
    }

    setOgTag(ogType, newVal) {
        const selector = `property="og:${ogType}"`;
        if (this.meta.getTag(selector)) {
            this.meta.updateTag({property: `og:${ogType}`, content: newVal}, selector);
        }
    }

    setNoIndexNoFollow() {
        const tag = {name: 'robots', content: 'noindex, nofollow'};
        if (this.meta.getTag('name="robots"')) {
            this.meta.updateTag(tag, 'name="robots"');
        } else {
            this.meta.addTag(tag);
        }
    }

    setIndexFollow() {
        const tag = {name: 'robots', content: 'index, follow'};
        if(/\.(signature-it|signatureit)\.(app|com)$/.test(location.hostname)){
            tag.content = 'noindex, nofollow';
        }
        if (this.meta.getTag('name="robots"')) {
            this.meta.updateTag(tag, 'name="robots"');
        } else {
            this.meta.addTag(tag);
        }
    }

    getSeo(url, langId) {
        const newUrl = url.split('#')[0]; // Split the URL by '#'
        return this.http.get<any>(`${this.seoUrl}${newUrl}&lang=${langId}`)
            .pipe(map((res) => {
                if (res) {
                    return SEODTO.create(res);
                }
                return;
            }), catchError((error: any) => throwError(error)));
    }

    getCampaignSeo(url, langId) {
        return this.http.get<any>(`${this.cseoUrl}${url}&lang=${langId}`)
            .pipe(map((res) => SEODTO.create(res)), catchError((error: any) => throwError(error)));
    }

	getEntitySeo(uri, type, langId) {
		return this.http.get<any>(`${this.eseoUrl}${uri}&type=${type}&lang=${langId}`)
			.pipe(map((res) => {
				if (res && res['Seo_Title']) {
					return SEODTO.create(res);
				}
				return;
			}), catchError((error: any) => throwError(error)));
	}


    updatePageData(key, value) {
        this.pageData[key] = value;
        if(GenericConfig.showProductStructureData) {
            this.prepareJson()
        }
    }


    loadJson(): Observable<any> {
        if(!this.fileJson) {
            this.fileJson = this.http.get('/assets/product-data.json').pipe(
                shareReplay(1)
            );
        }
        return this.fileJson;
    }
    prepareJson() {
        this.loadJson().subscribe(res => {
            let pageJson;
            for(let key in res) {
                if(location.pathname.match(`/${key}/`)) {
                    pageJson = res[key];
                    break;
                }
            }
            if(pageJson) {
                let json = this.transform(pageJson, this.pageData);
                this.setScript(json);
            }
        })
    }

    setScript(content){
        if(this.schemaScript) {
            this.schemaScript.text = content;
        } else  {
            this.schemaScript = document.createElement('script');
            this.schemaScript.type = 'application/ld+json';
            this.schemaScript.text = content;
            document.getElementsByTagName('head')[0].appendChild(this.schemaScript);
        }
    }

    transform(json, replacements){
        let jsonString = JSON.stringify(json);
        const regex = /"%%([\w.]+)%%"/g;
        return jsonString.replace(regex, ($1, $2) => {
            let  fieldVal = $2.split('.').reduce((acc, key) => acc?.[key], replacements) || '';
            switch (true) {
                case !fieldVal:
                    return '""';
                case Array.isArray(fieldVal):
                    return JSON.stringify(fieldVal);
                default:
		    fieldVal = fieldVal.toString().replace('"', '\"');
                    return `"${fieldVal}"`;
            }
        });
    }

}
